import { createSelector } from 'reselect'
import { get, values, find } from 'lodash'

import { getAttendantSettings } from './attendantSettings'

export const MENU_TYPES = ['preorder', 'day_of_event', 'coffee', 'cordial', 'dessert', 'restock']

export const getManagerIds = (state) => state?.attendant.managerEmployeeIds
export const getEmployeeIds = (state) => state?.attendant.employeeIds

export const getAllAttendants = (state) => state?.attendant.byId || {}
/**
 * @deprecated use getAllAttendants instead as "values" returns a new array each time
 */
export const getAttendants = (state) => values(get(state, 'attendant.byId'))

export const getAttendantsMap = (state) => state?.attendant?.byId || {}

export const hasAttendants = createSelector(
  (state) => state.attendant.byId,
  (attendantsById) => !!attendantsById && Object.keys(attendantsById).length > 0,
)

export const getAttemptPinLoginStatus = (state) => get(state, 'attendant.attemptPinLoginStatus')

export const getEmployeeApprovalCodes = createSelector(getAttendants, (attendants) => {
  const attendantPins = attendants.map((attendant) => attendant.pinNumber)

  return attendantPins
})

/**
 * Status of fetching attendants request
 *
 * @param {*} state
 * @returns {String} 'loading' | 'succeeded' | 'failed'
 */
export const getFetchAttendantsStatus = (state) => get(state, 'attendant.fetchAttendantsStatus')

export const getActiveAttendantId = (state) => get(state, 'attendant.activeAttendantId')

export const makeGetAttendant = () =>
  createSelector(
    getAttendants,
    (_, attendantId) => attendantId,
    (attendants, attendantId) => find(attendants, ['uuid', attendantId]),
  )

// employee id and uuid can be different
export const makeGetAttendantByEmployeeId = () =>
  createSelector(
    getAttendants,
    (_, employeeId) => employeeId,
    (attendants, employeeId) => find(attendants, ['employeeId', employeeId]),
  )

export const makeGetAttendantByPin = () =>
  createSelector(
    getAttendants,
    (_, pin) => pin,
    (attendants, pin) => find(attendants, ['pinNumber', pin]),
  )

export const getActiveAttendant = createSelector(
  getAllAttendants,
  getActiveAttendantId,
  (attendantsById, activeAttendantId) => attendantsById[activeAttendantId],
)

export const makeGetActiveAttendant = () =>
  createSelector(
    getAttendants,
    getAttendantSettings,
    getActiveAttendantId,
    (attendants, attendantSettings, activeAttendantId) => {
      const attendant = find(attendants, ['uuid', activeAttendantId])
      const attendantSettingsId = get(attendant, 'attendantSettingsId')
      const settings = find(attendantSettings, ['uuid', attendantSettingsId])

      return {
        ...attendant,
        settings,
      }
    },
  )

export const getAttendantName = (state, employeeId) => {
  return state?.attendant?.byId?.[employeeId]?.name ?? ''
}

export const makeGetAttendantMenuTypes = () =>
  createSelector(
    /**
     * This is not the most efficient since the `(state) => state` selector will
     * change most of the time and not provide the benefit memoization.
     */
    makeGetActiveAttendant(),
    (attendantSettings) => {
      const hasAllAccess = ['manager', 'supervisor'].includes(attendantSettings?.employeeRole?.toLowerCase?.())
      const attendantMenuSubtypes = get(attendantSettings, 'settings.configuration.menuSubtypes')

      if (attendantMenuSubtypes === null || hasAllAccess) {
        return MENU_TYPES
      } else {
        return attendantMenuSubtypes ?? []
      }
    },
  )

export const makeGetAttendantStandUuids = () =>
  createSelector(
    /**
     * This is not the most efficient since the `(state) => state` selector will
     * change most of the time and not provide the benefit memoization.
     */
    makeGetActiveAttendant(),
    (state) => state,
    (attendantSettings, state) => {
      const attendantStandUuids = get(attendantSettings, 'settings.configuration.standUuids')
      const hasAllAccess = ['manager', 'supervisor'].includes(attendantSettings?.employeeRole?.toLowerCase?.())

      if (attendantStandUuids === null || hasAllAccess) {
        return Object.keys(state.stands.byId)
      } else {
        return attendantStandUuids ?? []
      }
    },
  )

export const getManagerId = (state) => state?.attendant?.managerId ?? ''
